<template>
  <div class="agent-profile-page">
    <!-- 加载状态 -->
    <div v-if="loading" class="loading-container">
      <i class="el-icon-loading loading-icon"></i>
      <p>加载中...</p>
    </div>

    <!-- 错误状态 -->
    <div v-else-if="error" class="error-container">
      <el-result
        icon="error"
        title="加载失败"
        :subTitle="error"
      >
        <template slot="extra">
          <el-button type="primary" @click="loadAgentInfo">重试</el-button>
        </template>
      </el-result>
    </div>

    <!-- 智能体信息展示 -->
    <div v-else-if="agentData" class="agent-content">
      <!-- Section 1: 数字员工工牌（完全复制happy项目） -->
      <section class="agent-card-section">
        <!-- 工牌吊绳框架 -->
        <div class="badge-card-frame">
          <!-- 吊绳SVG -->
          <div class="lanyard-svg">
            <svg width="180" height="100" viewBox="0 0 180 100">
              <defs>
                <!-- 渐隐效果：从下到上逐渐透明 -->
                <linearGradient id="fadeGradient" x1="0%" y1="100%" x2="0%" y2="0%">
                  <stop offset="0%" stop-color="#999" stop-opacity="0.5" />
                  <stop offset="70%" stop-color="#999" stop-opacity="0.3" />
                  <stop offset="100%" stop-color="#999" stop-opacity="0" />
                </linearGradient>
              </defs>

              <g>
                <!-- 左侧弧线 - 从夹扣处开始 -->
                <path
                  d="M 75 95 Q 70 70, 68 45 Q 67 25, 70 5"
                  stroke="url(#fadeGradient)"
                  stroke-width="2"
                  fill="none"
                  stroke-linecap="round"
                />

                <!-- 右侧弧线 - 从夹扣处开始，等距对称 -->
                <path
                  d="M 105 95 Q 110 70, 112 45 Q 113 25, 110 5"
                  stroke="url(#fadeGradient)"
                  stroke-width="2"
                  fill="none"
                  stroke-linecap="round"
                />

                <!-- 工牌夹扣 - 圆角矩形开孔，连接挂绳和工牌 -->
                <g>
                  <!-- 夹扣外框 -->
                  <rect
                    x="70"
                    y="88"
                    width="40"
                    height="10"
                    rx="5"
                    fill="#f0f2f5"
                    stroke="#dcdfe6"
                    stroke-width="1.5"
                  />
                  <!-- 夹扣高光 -->
                  <rect
                    x="72"
                    y="89"
                    width="36"
                    height="2.5"
                    rx="1.2"
                    fill="#ffffff"
                    opacity="0.3"
                  />
                  <!-- 中心开孔 - 圆角矩形 -->
                  <rect
                    x="82"
                    y="91"
                    width="16"
                    height="4"
                    rx="2"
                    fill="#ffffff"
                    stroke="#dcdfe6"
                    stroke-width="0.5"
                    opacity="0.6"
                  />
                </g>
              </g>
            </svg>
          </div>

          <!-- 工牌主体（倾斜2度） -->
          <div class="employee-badge">
            <!-- 顶部标识 -->
            <div class="badge-header">
              <i class="el-icon-medal"></i>
              <span>DIGITAL EMPLOYEE</span>
            </div>

            <!-- Avatar with Border -->
            <div class="avatar-wrapper">
              <div class="avatar-border">
                <head-image
                  :size="100"
                  :name="agentData.agentName"
                  :url="agentData.avatarUrl"
                  :style="{ borderRadius: '50%' }"
                ></head-image>
              </div>
            </div>

            <!-- 智能体名称 -->
            <h1 class="agent-name">{{ agentData.agentName }}</h1>

            <!-- ID -->
            <div class="agent-id">
              <span class="id-label">ID:</span>
              <span class="id-value">{{ agentData.agentId.slice(0, 16) }}</span>
            </div>

            <!-- 能力标签 -->
            <div class="capabilities" v-if="hasCapabilities">
              <div class="capability-item" v-if="agentData.skills && agentData.skills.length > 0">
                <i class="el-icon-lightning"></i>
                <span>{{ agentData.skills.length }} 个技能</span>
              </div>
              <div class="capability-item" v-if="agentData.mcp && agentData.mcp.servers && agentData.mcp.servers.length > 0">
                <i class="el-icon-link"></i>
                <span>{{ agentData.mcp.servers.length }} 个MCP</span>
              </div>
              <div class="capability-item" v-if="hasWorkflow">
                <i class="el-icon-share"></i>
                <span>工作流</span>
              </div>
            </div>

            <!-- 访问权限与价格 -->
            <div class="access-pricing-section" v-if="agentData.publishSettings">
              <div class="access-info">
                <i class="el-icon-lock"></i>
                <span>{{ formatAccessScope(agentData.publishSettings.accessScope) }}</span>
              </div>
              <div class="pricing-info" v-if="agentData.publishSettings.pricing">
                <i class="el-icon-price-tag"></i>
                <span v-if="agentData.publishSettings.pricing.isFree">免费使用</span>
                <span v-else>{{ formatPricing(agentData.publishSettings.pricing) }}</span>
              </div>
            </div>

            <!-- 岗位职责 -->
            <div class="divider" v-if="agentData.description"></div>
            <div class="description-section" v-if="agentData.description">
              <div class="description-title">岗位职责：</div>
              <div class="description-value">
                {{ agentData.description }}
              </div>
            </div>

            <!-- 签发日期 -->
            <div class="badge-footer">
              <span>ISSUED: {{ formatDate(agentData.createTime) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- Section 2: 使用说明 -->
      <section class="usage-section">
        <h2 class="section-title">用AI赋能超级个体</h2>
        <div class="hero-subtitle">AI营销自动化 + AI工具开发自动化<br>两大能力，让你的个人能力指数级增长</div>
        <div class="hero-features">
          <span class="feature-tag">✓ 中文版Claude Code</span>
          <span class="feature-tag">✓ AI创作军团</span>
          <span class="feature-tag">✓ 支持接入企微/钉钉/飞书</span>
          <span class="feature-tag">✓ 真正能干活</span>
        </div>
        <div class="stats-grid">
          <div class="stat-item">
            <div class="stat-value">10倍</div>
            <div class="stat-label">效率提升</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">60%</div>
            <div class="stat-label">成本降低</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">1000+</div>
            <div class="stat-label">预置智能体</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">0门槛</div>
            <div class="stat-label">上手即用</div>
          </div>
        </div>
      </section>

      <!-- Section 3: 案例展示 -->
      <section class="cases-section">
        <h2 class="section-title">应用案例</h2>
        <div class="cases-grid">
          <div class="case-card">
            <div class="case-icon">
              <i class="el-icon-edit-outline"></i>
            </div>
            <h3 class="case-title">内容创作</h3>
            <p class="case-description">
              智能体可以帮助您生成高质量的文章、报告和创意内容
            </p>
          </div>

          <div class="case-card">
            <div class="case-icon">
              <i class="el-icon-data-analysis"></i>
            </div>
            <h3 class="case-title">数据分析</h3>
            <p class="case-description">
              分析数据、生成报表，帮助您做出更明智的决策
            </p>
          </div>

          <div class="case-card">
            <div class="case-icon">
              <i class="el-icon-service"></i>
            </div>
            <h3 class="case-title">客户服务</h3>
            <p class="case-description">
              自动回答常见问题，提供7x24小时的客户支持
            </p>
          </div>
        </div>
      </section>

      <!-- Section 4: CTA按钮 -->
      <section class="cta-section" style="margin-bottom: 0px;height: 90px;padding: 0px;">
        <el-button
          type="primary"
          size="large"
          :loading="startChatLoading"
          @click="handleStartChat"
          class="cta-button"
        >
          {{ startChatLoading ? '正在初始化...' : `立即体验 ${agentData.agentName}` }}
        </el-button>
        <p class="cta-subtitle">
          {{ isLoggedIn ? '开始对话' : '登录后即可使用' }}
        </p>
      </section>
    </div>
  </div>
</template>

<script>
import HeadImage from "@/components/common/HeadImage.vue";

export default {
  name: "AgentProfile",
  components: { HeadImage },
  data() {
    return {
      loading: false,
      error: null,
      agentData: null,
      shareId: null,
      agentId: null,
      startChatLoading: false,
      historyListener: null
    };
  },
  computed: {
    isLoggedIn() {
      // 使用Vuex store的登录状态，而不是直接读取sessionStorage
      return this.$store.getters.isLoggedIn;
    },
    hasCapabilities() {
      if (!this.agentData) return false;
      return (
        (this.agentData.skills && this.agentData.skills.length > 0) ||
        (this.agentData.mcp && this.agentData.mcp.servers && this.agentData.mcp.servers.length > 0) ||
        this.hasWorkflow
      );
    },
    hasWorkflow() {
      if (!this.agentData) return false;
      return this.agentData.workflow && Object.keys(this.agentData.workflow).length > 0;
    }
  },
  async created() {
    // 尝试恢复登录状态
    await this.initializeLoginState();

    this.shareId = this.$route.params.shareId;
    this.agentId = this.$route.params.agentId;
    
    if (this.shareId || this.agentId) {
      await this.loadAgentInfo();
    } else {
      this.error = '分享链接无效';
      return;
    }

    // 检测是否从登录页返回，如果是则自动触发初始化
    const { autoStart } = this.$route.query;
    if (autoStart === 'true' && this.isLoggedIn && this.agentData) {
      this.$nextTick(() => {
        this.handleStartChat();
      });
    }
  },
  mounted() {
    // 在移动端隐藏浏览器导航按钮
    if (this.isMobileDevice()) {
      this.disableBrowserNavigation();
    }
  },
  beforeDestroy() {
    // 清理 history 监听器
    if (this.historyListener) {
      window.removeEventListener('popstate', this.historyListener);
    }
  },
  methods: {
    // 禁用浏览器导航（后退/前进按钮）
    disableBrowserNavigation() {
      // 方法1: 替换当前历史记录，阻止后退
      window.history.replaceState(null, '', window.location.href);
      window.history.pushState(null, '', window.location.href);

      // 方法2: 监听 popstate 事件，当用户点击后退时强制前进
      this.historyListener = () => {
        window.history.pushState(null, '', window.location.href);
      };
      window.addEventListener('popstate', this.historyListener);

      // 方法3: 尝试隐藏地址栏（部分浏览器支持）
      this.hideAddressBar();
    },

    // 尝试隐藏移动端地址栏
    hideAddressBar() {
      // 延迟滚动以隐藏地址栏
      setTimeout(() => {
        if (window.scrollY === 0) {
          window.scrollTo(0, 1);
        }
      }, 100);
    },

    async initializeLoginState() {
      try {
        // 优先从localStorage恢复用户信息（跨标签页共享）
        const savedUserInfo = localStorage.getItem('userInfo');

        if (savedUserInfo) {
          try {
            const userInfo = JSON.parse(savedUserInfo);

            // 如果localStorage中有用户信息且包含accessToken
            if (userInfo && userInfo.accessToken) {
              console.log('[AgentProfile] 从localStorage恢复用户信息');

              // 同步token到sessionStorage（保持现有逻辑兼容性）
              sessionStorage.setItem('accessToken', userInfo.accessToken);
              if (userInfo.refreshToken) {
                sessionStorage.setItem('refreshToken', userInfo.refreshToken);
              }
              if (userInfo.tokenKey) {
                sessionStorage.setItem('tokenKey', userInfo.tokenKey);
              }

              // 如果store中还没有登录状态，则设置
              if (!this.$store.getters.isLoggedIn) {
                this.$store.commit('setUserInfo', userInfo);
                this.$store.commit('setIsLoggedIn', true);
                console.log('[AgentProfile] 登录状态已恢复');
              }
              return;
            }
          } catch (err) {
            console.error('[AgentProfile] 解析localStorage中的userInfo失败:', err);
          }
        }

        // 如果localStorage中没有，尝试从sessionStorage恢复
        const accessToken = sessionStorage.getItem('accessToken');
        if (accessToken && !this.$store.getters.isLoggedIn) {
          console.log('[AgentProfile] 从sessionStorage检测到token，尝试加载用户信息');
          try {
            await this.$store.dispatch('loadUser');
            this.$store.commit('setIsLoggedIn', true);
            console.log('[AgentProfile] 登录状态恢复成功');
          } catch (err) {
            console.error('[AgentProfile] 恢复登录状态失败:', err);
            // 如果加载失败，清除无效token（同时清理 localStorage 和 sessionStorage）
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('refreshToken');
            sessionStorage.removeItem('tokenKey');
            localStorage.removeItem('userInfo');
          }
        }
      } catch (error) {
        console.error('[AgentProfile] 初始化登录状态失败:', error);
        // 清理可能损坏的存储数据
        try {
          sessionStorage.removeItem('accessToken');
          sessionStorage.removeItem('refreshToken');
          sessionStorage.removeItem('tokenKey');
          localStorage.removeItem('userInfo');
        } catch (e) {
          console.error('[AgentProfile] 清理存储失败:', e);
        }
      }
    },

    async loadAgentInfo() {
      try {
        this.loading = true;
        this.error = null;

        const url = this.agentId 
          ? `/agent/public/by-agent-id/${this.agentId}`
          : `/agent/public/${this.shareId}`;
        
        const agentData = await this.$http({
          url,
          method: 'get'
        });

        this.agentData = agentData;
      } catch (err) {
        console.error('[AgentProfile] 获取智能体信息失败:', err);
        if (err.response && err.response.status === 404) {
          this.error = '智能体不存在或未开启分享';
        } else {
          this.error = err.message || '获取智能体信息失败，请稍后重试';
        }
      } finally {
        this.loading = false;
      }
    },

    formatDate(dateString) {
      if (!dateString) {
        return new Date().toISOString().split('T')[0];
      }
      try {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
      } catch (e) {
        return new Date().toISOString().split('T')[0];
      }
    },

    formatAccessScope(scope) {
      const map = { PUBLIC: '公开访问', FRIENDS: '仅好友可用', GROUP_MEMBERS: '仅群成员可用' };
      return map[scope] || '公开访问';
    },

    formatPricing(pricing) {
      if (!pricing) return '免费使用';
      const symbol = { CNY: '¥', USD: '$', EUR: '€' }[pricing.currency] || '¥';
      const parts = [];
      if (pricing.perUsePrice > 0) parts.push(`${symbol}${pricing.perUsePrice}/次`);
      if (pricing.monthlyPrice > 0) parts.push(`${symbol}${pricing.monthlyPrice}/月`);
      if (pricing.quarterlyPrice > 0) parts.push(`${symbol}${pricing.quarterlyPrice}/季`);
      if (pricing.yearlyPrice > 0) parts.push(`${symbol}${pricing.yearlyPrice}/年`);
      return parts.length > 0 ? parts.join(' | ') : '免费使用';
    },

    async handleStartChat() {
      // 1. 登录检测
      if (!this.isLoggedIn) {
        this.$router.push({
          path: '/login',
          query: {
            redirect: this.$route.fullPath,
            autoStart: 'true'
          }
        });
        return;
      }

      // 2. 显示loading
      this.startChatLoading = true;

      try {
        // 3. 调用后端接口：一站式完成权限检查、加好友/加群、创建会话记录
        // 后端会根据智能体的 accessScope 自动决定会话类型和父会话ID
        const res = await this.$http.post('/agent/session/ensure', {
          agentId: this.agentData.agentId
        });

        const sessionData = res;

        // 检查是否需要等待审批
        if (sessionData && (sessionData.status === 'PENDING_FRIEND' || sessionData.status === 'PENDING_GROUP')) {
          this.$message.info(sessionData.pendingMessage || '已发送申请，等待验证');
          return;
        }

        // 4. 跳转到 ChatBox，触发 UI 初始化
        const isMobile = this.isMobileDevice();
        this.$router.push({
          path: '/home/chat',
          query: {
            agentId: this.agentData.agentId,
            autoStart: 'true',
            fullscreen: isMobile ? 'true' : undefined
          }
        });
      } catch (error) {
        console.error('启动智能体失败:', error);
        this.$message.error(error.message || '无法访问该智能体');
      } finally {
        this.startChatLoading = false;
      }
    },


    // 检测是否为移动端设备
    isMobileDevice() {
      // 方法1: User Agent 检测
      const ua = navigator.userAgent;
      const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(ua);

      // 方法2: 屏幕宽度检测（更可靠）
      const isSmallScreen = window.innerWidth <= 768;

      return isMobile || isSmallScreen;
    }
  }
};
</script>

<style  lang="scss">
#app{

  overflow: auto;
}
.agent-profile-page {
  background-color: #f4f5f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: #333;

  .loading-container,
  .error-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 80vh;
    .loading-icon {
      font-size: 48px;
    }
    p {
      font-size: 16px;
      color: #666;
      margin-top: 16px;
    }
  }

  .agent-content {
    max-width: 800px;
    margin: 0 auto;
    padding: 30px 15px 120px; // Extra padding at bottom for mobile CTA
  }

  .section-title {
    font-size: 26px;
    font-weight: 600;
    text-align: center;
    margin-bottom: 30px;
  }

  // Section 1: 智能体工牌 (Modernized)
  .agent-card-section {
    margin-bottom: 60px; // Add space before next section

    .badge-card-frame {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 80px; // Adjust for lanyard
      // Use a more subtle shadow
      filter: drop-shadow(0 4px 15px rgba(0, 0, 0, 0.08));

      .lanyard-svg {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100px;
        display: flex;
        justify-content: center;
        z-index: 10;
        // Ensure SVG is not affected by other transforms
        transform: translateZ(0);
      }

      .employee-badge {
        width: 100%;
        max-width: 340px;
        background-color: #ffffff;
        border-radius: 20px;
        padding: 24px;
        border: 1px solid #e5e7eb; // Softer border
        z-index: 1;
        position: relative;
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
        }

        // Removed &.rotated class for simplicity

        .badge-header {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          background-color: transparent;
          border: none;
          padding: 0;
          margin: 0 auto 18px;
          width: fit-content;

          span {
            font-size: 12px;
            letter-spacing: 1px;
            color: #9ca3af;
            font-weight: 500;
            text-transform: uppercase;
          }

          i {
            font-size: 14px;
            color: #9ca3af;
          }
        }

        .avatar-wrapper {
          display: flex;
          justify-content: center;
          margin-bottom: 16px;

          .avatar-border {
            border: 4px solid #fff;
            border-radius: 50%;
            padding: 0;
            background: transparent;
            box-shadow: 0 0 0 1px #e5e7eb;
          }
        }

        .agent-name {
          font-size: 24px;
          font-weight: 700;
          color: #1f2937;
          text-align: center;
          margin: 0 0 4px;
        }

        .agent-id {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          margin-bottom: 20px;
          font-size: 12px;
          background-color: #f9fafb;
          padding: 4px 10px;
          border-radius: 6px;
          width: fit-content;
          margin-left: auto;
          margin-right: auto;
          border: 1px solid #e5e7eb;

          .id-label {
            color: #6b7280;
            font-weight: 500;
          }

          .id-value {
            color: #4b5563;
            font-family: "SF Mono", "Fira Code", "Fira Mono", "Roboto Mono", monospace;
            font-weight: 500;
          }
        }

        .capabilities {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 8px;
          margin-top: 16px;
          margin-bottom: 24px;

          .capability-item {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            background-color: #edf2ff; // Lighter, modern blue
            border-radius: 16px;
            padding: 5px 12px;
            transition: background-color 0.3s ease;

            &:hover {
              background-color: #dee6ff;
            }

            i {
              font-size: 13px;
              color: #435fe3; // Primary theme color
            }

            span {
              font-size: 13px;
              color: #3b55d7;
              font-weight: 500;
            }
          }
        }

        .access-pricing-section {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 12px;
          margin-bottom: 20px;
          padding: 12px 16px;
          background: linear-gradient(135deg, #f8f9ff 0%, #fff5f5 100%);
          border-radius: 12px;

          .access-info, .pricing-info {
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 13px;
            
            i {
              font-size: 14px;
            }
          }

          .access-info {
            color: #52c41a;
            i { color: #52c41a; }
          }

          .pricing-info {
            color: #ff6b6b;
            font-weight: 500;
            i { color: #ff6b6b; }
          }
        }

        .divider {
          height: 1px;
          background-color: #f3f4f6; // simple, clean line
          margin: 20px 0;
        }

        .description-section {
          .description-title {
            font-size: 14px;
            font-weight: 600;
            color: #374151;
            text-align: left;
            margin-bottom: 8px;
          }

          .description-value {
            font-size: 14px;
            line-height: 1.6;
            color: #6b7280;
            text-align: left;
          }
        }

        .badge-footer {
          margin-top: 20px;
          text-align: right;
          padding-top: 12px;
          border-top: 1px solid #f3f4f6;

          span {
            font-size: 11px;
            color: #9ca3af;
            letter-spacing: 0.5px;
            font-weight: 400;
          }
        }
      }
    }
  }

  // Section 2: Hero Section (Modernized)
  .usage-section {
    background-color: #ffffff;
    border-radius: 16px;
    padding: 40px 32px;
    border: 1px solid #e5e7eb;
    text-align: center;

    .hero-subtitle {
      font-size: 16px;
      color: #6b7280;
      line-height: 1.8;
      margin-bottom: 24px;
    }

    .hero-features {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 12px;
      margin-bottom: 32px;

      .feature-tag {
        display: inline-block;
        background-color: #edf2ff;
        color: #4f46e5;
        padding: 8px 16px;
        border-radius: 20px;
        font-size: 14px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          background-color: #dbeafe;
          transform: translateY(-2px);
        }
      }
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 24px;
      margin-top: 32px;

      .stat-item {
        text-align: center;
        padding: 20px;
        background: linear-gradient(135deg, #f8f9ff 0%, #fff5f5 100%);
        border-radius: 12px;
        transition: transform 0.3s ease;

        &:hover {
          transform: translateY(-5px);
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #4f46e5;
          margin-bottom: 8px;
        }

        .stat-label {
          font-size: 14px;
          color: #6b7280;
          font-weight: 500;
        }
      }
    }
  }

  // Section 3: 案例展示 (Modernized)
  .cases-section {
    .cases-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); // Adjust minmax for better responsiveness
      gap: 24px;

      .case-card {
        background-color: #ffffff;
        border-radius: 16px;
        padding: 24px;
        text-align: center;
        border: 1px solid #e5e7eb;
        transition: transform 0.3s ease, box-shadow 0.3s ease;

        &:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0, 0, 0, 0.07);
        }

        .case-icon {
          width: 64px;
          height: 64px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, #818cf8, #a78bfa); // Softer purple gradient
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(129, 140, 248, 0.2);
          transition: transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

          i {
            font-size: 32px;
            color: white;
          }
        }

        &:hover .case-icon {
          transform: rotate(360deg) scale(1.1);
        }

        .case-title {
          font-size: 18px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 8px;
        }

        .case-description {
          font-size: 14px;
          color: #6b7280;
          line-height: 1.6;
          margin: 0;
        }
      }
    }
  }

  // Section 4: CTA (Modernized)
  .cta-section {
    text-align: center;
    padding: 40px 24px;
    margin: 40px 0 0;

    .cta-button {
      padding: 16px 48px;
      font-size: 18px;
      font-weight: 600;
      border-radius: 50px;
      background: linear-gradient(135deg, #4f46e5, #7c3aed); // Vibrant Indigo to Purple
      color: white;
      border: none;
      box-shadow: 0 8px 20px rgba(79, 70, 229, 0.3);
      transition: transform 0.3s ease, box-shadow 0.3s ease;
      cursor: pointer;

      &:hover {
        transform: translateY(-4px);
        box-shadow: 0 12px 30px rgba(79, 70, 229, 0.4);
      }

      &:active {
        transform: translateY(-2px);
      }
    }

    .cta-subtitle {
      font-size: 14px;
      color: #6b7280;
      margin: 12px 0 0;
      font-weight: 500;
    }
  }
}

// Desktop: Fixed CTA at bottom
@media (min-width: 769px) {
  .agent-profile-page {
    .agent-content {
      padding-bottom: 120px; // Space for fixed CTA
    }

    .cta-section {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 90px;
      padding: 16px;
      margin: 0;
      background: #ffffff;
      box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.05);
      z-index: 1000;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .cta-button {
        max-width: 400px;
      }
    }
  }
}

// Responsive Design (Corrected)
@media (max-width: 768px) {
  .agent-profile-page {
    .agent-content {
      padding: 24px 16px 120px; // 120px padding to avoid overlap with 90px fixed CTA + extra space

      section {
        margin-bottom: 40px;
      }

      .section-title {
        font-size: 22px;
        margin-bottom: 24px;
      }
    }

    // Mobile: Fixed CTA Bar
    .cta-section {
      height: 90px; // Explicit height for the CTA bar
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      padding: 16px;
      margin: 0;
      border-radius: 0;
      box-shadow: 0 -4px 15px rgba(0, 0, 0, 0.05);
      z-index: 1000;
      background: #ffffff;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;

      .cta-button {
        width: 100%;
        max-width: 400px;
        padding: 14px 24px;
        font-size: 16px;
      }

      .cta-subtitle {
        font-size: 12px;
        margin-top: 8px;
      }
    }

    // Agent Card Responsive Adjustments
    .agent-card-section {
      margin-bottom: 40px;

      .badge-card-frame {
        padding-top: 60px;

        .lanyard-svg {
          height: 80px;
          svg {
            width: 140px;
            height: 80px;
          }
        }

        .employee-badge {
          max-width: calc(100vw - 80px);
          padding: 20px;

          .badge-header {
            margin-bottom: 14px;
             span {
              font-size: 11px;
            }
             i {
              font-size: 13px;
            }
          }

          .avatar-wrapper {
            margin-bottom: 12px;
          }

          .agent-name {
            font-size: 20px;
          }

          .agent-id {
            margin-bottom: 16px;
          }

          .capabilities {
            gap: 6px;
            margin-bottom: 20px;

            .capability-item {
              padding: 4px 10px;
               i {
                font-size: 12px;
              }
               span {
                font-size: 12px;
              }
            }
          }

          .description-value {
            font-size: 13px;
          }
        }
      }
    }

    // Usage Section Responsive Adjustments
    .usage-section {
      padding: 32px 20px;

      .hero-subtitle {
        font-size: 14px;
        margin-bottom: 20px;
      }

      .hero-features {
        gap: 8px;
        margin-bottom: 24px;

        .feature-tag {
          font-size: 12px;
          padding: 6px 12px;
        }
      }

      .stats-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 16px;

        .stat-item {
          padding: 16px;

          .stat-value {
            font-size: 24px;
          }

          .stat-label {
            font-size: 12px;
          }
        }
      }
    }

    // Cases Section Responsive Adjustments
    .cases-section {
      .cases-grid {
        grid-template-columns: 1fr;
        gap: 16px;

        .case-card {
          padding: 24px;
          .case-icon {
            width: 56px;
            height: 56px;
            margin-bottom: 16px;
             i {
              font-size: 28px;
            }
          }
          .case-title {
            font-size: 16px;
            margin-bottom: 6px;
          }
          .case-description {
            font-size: 13px;
          }
        }
      }
    }
  }
}
</style>
